import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Smartphone, Download, AlertCircle } from 'lucide-react';
import api from '@/services/api';

const UpdateAppModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [updateData, setUpdateData] = useState(null);
    const [isForced, setIsForced] = useState(false);

    useEffect(() => {
        const checkVersion = async () => {
            if (!Capacitor.isNativePlatform()) return;

            try {
                const response = await api.get('superadmin/app-version/current/');
                const data = response.data;
                const info = await CapacitorApp.getInfo();
                
                const platform = Capacitor.getPlatform(); // 'ios' or 'android'
                const currentBuild = parseInt(info.build) || 0;
                const targetBuild = parseInt(platform === 'ios' ? data.ios_build : data.android_build) || 0;
                const targetVersion = platform === 'ios' ? data.ios_version : data.android_version;
                const storeUrl = platform === 'ios' ? data.ios_url : data.android_url;

                if (targetBuild > currentBuild && targetBuild > 0) {
                    setUpdateData({
                        version: targetVersion,
                        build: targetBuild,
                        url: storeUrl
                    });
                    const forceUpdate = platform === 'ios' ? data.ios_force_update : data.android_force_update;
                    setIsForced(forceUpdate);
                    setIsOpen(true);
                }
            } catch (error) {
                console.error('[UpdateCheck] Failed to check for updates:', error);
            }
        };

        checkVersion();
    }, []);

    const handleUpdate = () => {
        if (updateData?.url) {
            window.open(updateData.url, '_system');
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog 
            open={isOpen} 
            onOpenChange={isForced ? () => {} : setIsOpen}
        >
            <DialogContent 
                showCloseButton={false}
                className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] max-w-[88vw] sm:max-w-[380px] p-0 overflow-hidden rounded-2xl shadow-2xl"
                onPointerDownOutside={isForced ? (e) => e.preventDefault() : undefined}
                onEscapeKeyDown={isForced ? (e) => e.preventDefault() : undefined}
            >
                <div className="relative pt-8 sm:pt-10 pb-6 px-5 sm:px-6 text-center">
                    {/* Background decoration */}
                    <div className="absolute top-0 left-0 w-full h-24 sm:h-28 bg-gradient-to-b from-[hsl(var(--green-bright))]/20 to-transparent" />
                    
                    <div className="relative">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[hsl(var(--green-bright))] rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-5 shadow-lg shadow-[hsl(var(--green-bright))]/30 rotate-3">
                            <Smartphone className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                        </div>

                        <DialogHeader className="mb-4 sm:mb-5">
                            <DialogTitle className="text-lg sm:text-xl font-bold text-foreground mb-1.5 sm:mb-2">
                                New Update Available!
                            </DialogTitle>
                            <DialogDescription className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                                A newer and better version of Budlee AI is waiting for you in the store.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="bg-[hsl(var(--muted))]/30 rounded-xl p-3 sm:p-3.5 mb-5 sm:mb-6 border border-[hsl(var(--card-border))]">
                            <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5 sm:mb-1">Available Version</div>
                            <div className="text-base sm:text-lg font-bold text-[hsl(var(--green-bright))]">
                                v {updateData?.version} ({updateData?.build})
                            </div>
                        </div>

                        <div className="space-y-2.5 sm:space-y-3">
                            <Button 
                                onClick={handleUpdate}
                                className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white h-10 sm:h-12 text-sm sm:text-base font-bold rounded-xl shadow-lg shadow-emerald-600/20 border-0 transition-all active:scale-95"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Update Now
                            </Button>

                            {!isForced && (
                                <Button 
                                    variant="ghost" 
                                    onClick={() => setIsOpen(false)}
                                    className="w-full text-xs sm:text-sm text-muted-foreground hover:text-foreground h-9 sm:h-10"
                                >
                                    Maybe Later
                                </Button>
                            )}
                        </div>

                        {isForced && (
                            <div className="mt-4 sm:mt-5 flex items-center justify-center gap-1.5 text-amber-500 bg-amber-500/10 py-1.5 px-3 rounded-full">
                                <AlertCircle className="w-3.5 h-3.5" />
                                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Required Update</span>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default UpdateAppModal;
